/**
 * 모빌리티 허브 가격 헬퍼 (설계 §2-4)
 *
 * **새 가격 규칙을 만들지 않는다.** 대표가는 카드(`RealProductCard`)가 쓰는
 * `Product.price_per_day` 그대로고, 기간 총액은 daily v2 구간 선택 함수
 * (`getDailyV2RateForDays`)에 상품의 기간 티어를 그대로 먹인다. 마크업은 두 값
 * 모두 데이터 접근 계층의 `markUpProductPrices` 가 이미 올린 상태로 들어온다
 * (카드 대표가와 완전히 같은 경로 — 여기서 다시 곱하면 이중 마크업이 된다).
 */

import type { Product } from "@/types";
import { customerPolicyTotalForDays, parsePricingSource } from "@/lib/pricing/base-plus-extra";
import {
  getDailyV2RateForDays,
  isValidDailyPrice,
  type DailyTier,
} from "@/lib/utils/daily-matrix";

/**
 * 모델 시트 요금 그리드의 기간 (설계 §2-4).
 *
 * ⚠️ 목록 쿼리는 `day_prices` 임베드를 **이 네 구간으로 잘라서** 가져온다
 * (`queries.LIST_DAY_PRICE_MIN_DAYS` — 상품당 30행 → 4행, 검색 타임아웃 대응).
 * 1·2·7·30 에서는 전체 사다리와 답이 같지만(각 구간의 시작점이라 정확히 그 행이
 * 선택된다), 목록 경로 상품에 **다른 N 을 물으면 한 단계 아래 구간가**가 나온다.
 * 두 상수는 항상 같은 집합이어야 한다.
 */
export const PERIOD_DAYS = [1, 2, 3, 7, 30] as const;
export type PeriodDays = (typeof PERIOD_DAYS)[number];

/**
 * 카드와 동일한 대표가(최저 1일가). `markUpProductPrices` 가 이미 적용된 표시가다.
 */
export function representativeDailyPrice(product: Product): number | null {
  const price = Number(product?.price_per_day);
  return isValidDailyPrice(price) ? price : null;
}

/**
 * 세부 상품(sub_display_order) 축으로 묶은 기간 티어.
 * 좌표가 없는(=v1 모양) 데이터는 단일 그룹으로 취급한다.
 */
function tiersBySub(product: Product): Map<number, DailyTier[]> {
  const map = new Map<number, DailyTier[]>();
  for (const row of product?.day_prices ?? []) {
    const minDays = Number(row.min_days);
    const price = Number(row.price_per_day);
    // 0원 행도 **그대로 담는다** — 구간 선택 집합을 깎으면 서버/Edge 와 단가가
    // 갈라진다(daily-matrix.ts `mergeDailyMatrixConfig` 주석과 같은 규약).
    if (!Number.isFinite(minDays) || !Number.isFinite(price)) continue;
    const sub = row.sub_display_order ?? 0;
    const list = map.get(sub) ?? [];
    list.push({ minDays, pricePerDay: price });
    map.set(sub, list);
  }
  for (const list of map.values()) list.sort((a, b) => a.minDays - b.minDays);
  return map;
}

/**
 * `days`일 대여 시 **일 단가** — 세부 상품이 여러 개면 그중 최저(유효한 것만).
 * 티어가 없으면 null.
 */
export function periodDailyRate(product: Product, days: number): number | null {
  if (!Number.isFinite(days) || days <= 0) return null;
  let min = Infinity;
  for (const tiers of tiersBySub(product).values()) {
    const rate = getDailyV2RateForDays(days, tiers);
    if (!isValidDailyPrice(rate)) continue; // 0원 = 판매 불가 = 가격 없음
    if (rate < min) min = rate;
  }
  return min === Infinity ? null : min;
}

/** 기간 요금(기본+추가일) 상품인가 — 서버 표가 있거나, 옛 모양처럼 원본 요금이 남아 있으면. */
export function hasPolicyPricing(product: Product): boolean {
  return product.policyPeriodTotals != null || parsePricingSource(product.specifications) != null;
}

/**
 * `days`일 총액 = 구간 단가 × days (설계 §2-4).
 *
 * 기간 티어가 없는 상품(v1 · 티어 미등록)은 대표가 × days 로 폴백한다 — 요금
 * 그리드가 통째로 비는 것보다 "할인 없는 총액"이 사실에 가깝다. 두 경로 모두
 * 마크업이 이미 반영된 표시가만 쓴다.
 */
export function periodTotal(product: Product, days: number): number | null {
  if (!Number.isFinite(days) || days <= 0) return null;
  // 서버가 미리 계산한 고객가 표(요율·공급가 없이) — 기간 요금 상품은 이것만 읽는다.
  // 표에 없는 일수는 null(가격 없음): 목록·허브는 1~30일(허브 바이크는 요금 그리드 구간)만 싣는다.
  if (product.policyPeriodTotals) {
    const value = product.policyPeriodTotals[days];
    return value != null && value > 0 ? value : null;
  }
  // 옛 캐시 엔트리(표 없이 원본 요금이 남은 행)·비마크업 파트너 — 예전 계산 그대로.
  const policy = parsePricingSource(product.specifications);
  if (policy) {
    const totals = Object.keys(policy.subProducts).map((sub) => customerPolicyTotalForDays({
      specifications: product.specifications,
      subDisplayOrder: Number(sub),
      days,
      currency: product.currency || "USD",
      markupRate: product.pricingMarkupRate,
    })).filter((value): value is number => value != null && value > 0);
    return totals.length ? Math.min(...totals) : null;
  }
  const rate = periodDailyRate(product, days);
  if (rate != null) return rate * days;
  const base = representativeDailyPrice(product);
  return base == null ? null : base * days;
}
